import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { ArrowLeft, Globe, Save } from 'lucide-react';
import { toast } from 'sonner';
import { COUNTRIES } from '../constants/Countries';
import { createCountryWhitelist } from '../services/countryWhitelistService';

export default function WhitelistCreate() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const showId = searchParams.get('showId');
  const [whitelistName, setWhitelistName] = useState('');
  const [selectedCountries, setSelectedCountries] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const filteredCountries = COUNTRIES.filter(country =>
    country.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    country.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCountryToggle = (code: string) => {
    setSelectedCountries(prev => {
      const newSet = new Set(prev);
      if (newSet.has(code)) {
        newSet.delete(code);
      } else {
        newSet.add(code);
      }
      return newSet;
    });
  };

  const handleSave = async () => {
    if (!whitelistName.trim()) {
      toast.error('Please enter a whitelist name');
      return;
    }
    if (selectedCountries.size === 0) {
      toast.error('Please select at least one country');
      return;
    }

    setIsCreating(true);

    try {
      const countryCodes = Array.from(selectedCountries);

      await createCountryWhitelist({
        displayName: whitelistName.trim(),
        countryCodes
      });

      toast.success(`Whitelist "${whitelistName}" created successfully!`);
      navigate(showId ? `/publish/${showId}` : '/publish');
    } catch (error: any) {
      console.error('Failed to create whitelist:', error);
      toast.error(error?.message || 'Failed to create whitelist');
    } finally {
      setIsCreating(false);
    }
  };

  const handleSelectAll = () => {
    const visibleCodes = new Set(filteredCountries.map(c => c.code));
    setSelectedCountries(prev => {
      const newSet = new Set(prev);
      visibleCodes.forEach(code => newSet.add(code));
      return newSet;
    });
  };

  const handleDeselectAll = () => {
    const visibleCodes = new Set(filteredCountries.map(c => c.code));
    setSelectedCountries(prev => {
      const newSet = new Set(prev);
      visibleCodes.forEach(code => newSet.delete(code));
      return newSet;
    });
  };

  return (
    <div className="h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="h-[49px] border-b sticky top-0 bg-background z-50">
        <div className="h-full flex items-center px-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(showId ? `/publish/${showId}` : '/publish')}
            className="mr-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Publish
          </Button>
          <h1 className="font-medium text-sm">Create Whitelist</h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-[800px] mx-auto px-6 py-8">
          <div className="space-y-6">
            {/* Page Title */}
            <div className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-muted-foreground" />
              <h2 className="text-lg font-semibold">Create New Whitelist</h2>
            </div>

            {/* Whitelist Name */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                Whitelist Name
                <span className="text-primary">*</span>
              </Label>
              <Input
                placeholder="Enter whitelist name (e.g., Europe + US)"
                value={whitelistName}
                onChange={(e) => setWhitelistName(e.target.value)}
              />
            </div>

            {/* Country Selection */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Select Countries</Label>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSelectAll}
                  >
                    Select All Visible
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDeselectAll}
                  >
                    Deselect All
                  </Button>
                </div>
              </div>

              {/* Search */}
              <Input
                placeholder="Search countries..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="mb-4"
              />

              {/* Selected Count */}
              <p className="text-sm text-muted-foreground">
                {selectedCountries.size} {selectedCountries.size === 1 ? 'country' : 'countries'} selected
              </p>

              {/* Countries Grid */}
              <div className="border rounded-lg p-4 max-h-[400px] overflow-y-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {filteredCountries.map((country) => (
                    <div
                      key={country.code}
                      className="flex items-center space-x-2 p-2 rounded hover:bg-secondary/50 cursor-pointer"
                      onClick={() => handleCountryToggle(country.code)}
                    >
                      <Checkbox
                        id={country.code}
                        checked={selectedCountries.has(country.code)}
                        onCheckedChange={() => handleCountryToggle(country.code)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <Label
                        htmlFor={country.code}
                        className="text-sm font-normal cursor-pointer flex-1"
                      >
                        {country.label}
                      </Label>
                    </div>
                  ))}
                </div>

                {filteredCountries.length === 0 && (
                  <p className="text-center text-muted-foreground py-8">
                    No countries found matching "{searchTerm}"
                  </p>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => navigate(showId ? `/publish/${showId}` : '/publish')}
                disabled={isCreating}
              >
                Cancel
              </Button>
              <Button
                className="flex-1"
                onClick={handleSave}
                disabled={isCreating || !whitelistName.trim() || selectedCountries.size === 0}
              >
                {isCreating ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    Creating...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Create Whitelist
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}